import type { JSX } from "react";

import { IconButton, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import {
  SubstructureLabel,
  SubstructureScrollRestoreBehavior,
} from "@plansoft-configuration-renderer/components";
import { ConfigurationObjectResult } from "@plansoft-configuration-renderer/configuration-api";
import { useCsConfigurationApi } from "@plansoft-configuration-renderer/framework";

import { RegioluxAddIcon, RegioluxMinusIcon } from "@/icons/RegioluxIcons";
import { customPalette } from "@/theme/theme";

type CustomProductCardProps = {
  substructure: ConfigurationObjectResult;
};

export function CustomProductCard({
  substructure,
}: CustomProductCardProps): JSX.Element {
  const api = useCsConfigurationApi();

  const quantityData = substructure.properties?.quantityControl
    ? JSON.parse(substructure.properties.quantityControl.value)
    : undefined;

  async function handleIncreaseQuantity(): Promise<void> {
    await api.invokeUserDefinedFunction({
      name: "increaseQuantity",
      path: "/",
      input: `/XAS_GERAETETRAEGER/${substructure.index}`,
    });
  }

  async function handleDecreaseQuantity(): Promise<void> {
    await api.invokeUserDefinedFunction({
      name: "decreaseQuantity",
      path: "/",
      input: `/XAS_GERAETETRAEGER/${substructure.index}`,
    });
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SubstructureLabel substructure={substructure} />
        {quantityData && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton
              disabled={!quantityData.isMinusAvailable}
              onClick={handleDecreaseQuantity}
              sx={{
                fontSize: "0.75rem",
              }}
            >
              <RegioluxMinusIcon />
            </IconButton>
            <Box>{quantityData.quantity}</Box>
            <IconButton
              disabled={!quantityData.isAddAvailable}
              onClick={handleIncreaseQuantity}
              sx={{
                fontSize: "0.75rem",
              }}
            >
              <RegioluxAddIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box display={"flex"} gap={"0.5rem"}>
        <img
          src={
            substructure.image
              ? substructure.image
              : "/unfinishedConfiguration.png"
          }
          style={{
            width: "40%",
            backgroundColor: customPalette.primary.light,
          }}
        />
        <Box sx={{ maxWidth: "50%" }}>
          <Typography variant={"body2"}>
            {substructure.properties?.product
              ? substructure.properties?.product.value
              : ""}
          </Typography>
        </Box>
        <SubstructureScrollRestoreBehavior instancePath={substructure.path} />
      </Box>
    </Box>
  );
}
